'use client';

import { createTheme } from '@mui/material/styles';

/* ═══════════════════════════════════════════════════════════════
   Unified Design System — "Editorial Traveler"
   Matches globals.css CSS variables exactly.
   Primary: Signal Blue #0058bc (light) / #adc6ff (dark)
   ═══════════════════════════════════════════════════════════════ */

const sharedTypography = {
  fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  h1: { fontWeight: 700, letterSpacing: '-0.028em' },
  h2: { fontWeight: 700, letterSpacing: '-0.028em' },
  h3: { fontWeight: 600, letterSpacing: '-0.02em' },
  h4: { fontWeight: 600, letterSpacing: '-0.02em' },
  h5: { fontWeight: 600, letterSpacing: '-0.015em' },
  h6: { fontWeight: 600, letterSpacing: '-0.01em' },
  button: { fontWeight: 600, letterSpacing: '-0.01em' },
};

const sharedComponents = {
  MuiCssBaseline: {
    styleOverrides: {
      // Body background handled by MUI palette.background.default
      // which is aligned with globals.css --surface values
      body: {
        // Remove any conflicting backgrounds — MUI default works
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        border: '1px solid var(--border)',
        backgroundImage: 'none',
        transition: 'transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s cubic-bezier(0.4,0,0.2,1)',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        textTransform: 'none' as const,
        fontWeight: 600,
        borderRadius: 12,
        padding: '8px 20px',
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #0058bc 0%, #0070eb 100%)',
        boxShadow: '0px 4px 16px rgba(0, 88, 188, 0.25)',
        '&:hover': {
          background: 'linear-gradient(135deg, #0063d6 0%, #007dff 100%)',
          boxShadow: '0px 6px 20px rgba(0, 88, 188, 0.35)',
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined' as const,
      size: 'small' as const,
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 12,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        borderRadius: 10,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
        boxShadow: 'none',
        borderBottom: '1px solid var(--border)',
      },
    },
  },
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0058bc', light: '#0070eb', dark: '#004fa8' },
    secondary: { main: '#8b6fd6', light: '#a98de8', dark: '#6b4fb6' },
    background: {
      default: '#faf9fe',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a1b1f',
      secondary: '#414755',
    },
    error: { main: '#c0392b' },
    warning: { main: '#c97800' },
    info: { main: '#0058bc' },
    success: { main: '#1e8c5a' },
    divider: 'rgba(193, 198, 215, 0.25)',
  },
  shape: { borderRadius: 12 },
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          ...sharedComponents.MuiCard.styleOverrides.root,
          backgroundColor: '#ffffff',
          boxShadow: '0px 2px 12px rgba(26, 27, 31, 0.06)',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#adc6ff', light: '#c8daff', dark: '#8caee8' },
    secondary: { main: '#b9a2f0', light: '#cdbaf5', dark: '#9980d8' },
    background: {
      default: '#111318',
      paper: '#1a1b1f',
    },
    text: {
      primary: '#e3e2ea',
      secondary: '#c4c6d0',
    },
    error: { main: '#e55347' },
    warning: { main: '#f5c842' },
    info: { main: '#adc6ff' },
    success: { main: '#4ec98a' },
    divider: 'rgba(255, 255, 255, 0.08)',
  },
  shape: { borderRadius: 12 },
  typography: sharedTypography,
  components: {
    ...sharedComponents,
    MuiCard: {
      styleOverrides: {
        root: {
          ...sharedComponents.MuiCard.styleOverrides.root,
          backgroundColor: '#1a1b1f',
          boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.35)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          ...sharedComponents.MuiButton.styleOverrides.root,
          '&.MuiButton-containedPrimary': {
            background: 'linear-gradient(135deg, #4d88e0 0%, #6ba4f5 100%)',
            color: '#0d1a30',
            boxShadow: '0px 4px 16px rgba(77, 136, 224, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5a96f0 0%, #78b2ff 100%)',
              boxShadow: '0px 6px 20px rgba(77, 136, 224, 0.4)',
            },
          },
        },
      },
    },
  },
});
